import { faMedal, faTriangleExclamation } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { CellContext, ColumnDef } from '@tanstack/react-table';
import classNames from 'classnames';
import type { TFunction } from 'i18next';
import { Observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';

import type { ICampaignPartnerStats } from '@feathr/blackbox';
import type { TCollectionTableSort } from '@feathr/components';
import { LinkNameCell, NumberCell, TanStackHeader, Text, Tooltip } from '@feathr/components';
import type { useStore } from '@feathr/report_components/state';

import { getRank, isSortDescending, isStatisticalSort } from './PartnersStatsTableCard.utils';

import * as styles from './PartnersStatsColumns.css';

import noImg from 'images/no-img.png';

interface IProps {
  localUrl: ((url: string) => string) | undefined;
  pagination: Record<string, number>;
  partners: ReturnType<typeof useStore>['Partners'];
  sort: TCollectionTableSort;
  t: TFunction;
  totalCount: number;
}

type TCell = CellContext<ICampaignPartnerStats, unknown>;

function getStatColumn(
  id: 'clicks' | 'conversions' | 'leads' | 'views',
  title: string,
  size: number,
): ColumnDef<ICampaignPartnerStats> {
  return {
    id,
    accessorFn: (row): number => row[id],
    header: () => <TanStackHeader title={title} />,
    meta: { align: 'right' },
    size,
    sortDescFirst: false,
    cell: ({ row }: TCell): JSX.Element => <NumberCell format={'count'} value={row.original[id]} />,
  };
}

export default function partnersStatsColumns({
  localUrl,
  pagination,
  partners,
  sort,
  t,
  totalCount,
}: IProps): Array<ColumnDef<ICampaignPartnerStats>> {
  const isDescending = isSortDescending(sort);
  const isStatSort = isStatisticalSort(sort);
  const rankParams = { sort, totalCount, page: pagination.page, pageSize: pagination.page_size };

  return [
    {
      id: 'rank',
      header: () => <TanStackHeader title={t('Rank')} />,
      enableSorting: false,
      meta: { align: 'center' },
      size: 70,
      cell: ({ row }: TCell): JSX.Element => {
        const { index } = row;
        const rank = getRank(index, rankParams);
        const showMedals = isDescending && isStatSort && pagination.page === 0 && index <= 2;
        return (
          <div className={classNames(styles.rank, { [styles.primary]: !showMedals })}>
            {showMedals ? (
              <FontAwesomeIcon
                className={classNames({
                  [styles.gold]: index === 0,
                  [styles.silver]: index === 1,
                  [styles.bronze]: index === 2,
                })}
                icon={faMedal}
              />
            ) : (
              <>{rank ?? '—'}</>
            )}
          </div>
        );
      },
    },
    {
      id: 'name',
      accessorFn: (row): string => row.p_id,
      header: () => <TanStackHeader title={t('Name')} />,
      meta: { isFlex: true },
      size: 300,
      sortDescFirst: false,
      cell: ({ row }: TCell): JSX.Element => (
        <Observer>
          {function useAnonymousFunction(): JSX.Element {
            const partner = partners.get(row.original.p_id);
            const logo = partner.get('logo') || noImg;
            const name = partner.get('name') ?? '';
            const warning = row.original.error ? (
              <Tooltip
                className={styles.tooltip}
                title={t(
                  "This partner's stats are under-reporting because this partner generated more activity than can be tracked.",
                )}
              >
                <FontAwesomeIcon icon={faTriangleExclamation} size={'lg'} />
              </Tooltip>
            ) : undefined;
            return (
              <div className={styles.nameColumn}>
                <div
                  className={styles.logo}
                  style={{ ['--l-background-image' as any]: `url('${logo}')` }}
                />
                {localUrl ? (
                  <LinkNameCell
                    label={name}
                    to={localUrl(partner.getItemUrl())}
                    trailing={warning}
                  />
                ) : (
                  <>
                    <Text.DefaultRegular>{name}</Text.DefaultRegular>
                    {warning}
                  </>
                )}
              </div>
            );
          }}
        </Observer>
      ),
    },
    getStatColumn('clicks', t('Clicks'), 100),
    getStatColumn('views', t('Views'), 100),
    getStatColumn('leads', t('Leads'), 100),
    getStatColumn('conversions', t('Conversions'), 120),
  ];
}
